/**
 * The Settings sections and their URLs (DEC-198, UI_SDD §R.14).
 *
 * Settings is reached from the user menu, like the Claude app: "Settings" is the
 * person's own (Profile, Appearance), "Workspace settings" is the admin's (General,
 * Members, Usage, Scan rules). Each section has its own URL so a link can point at the
 * exact place ("Add an AI key" lands on General, "Add your working hours" on Scan rules).
 */

import type { Profile } from '../../types/api'

/** The label of each profile (DEC-179), shared by the user menu and Profile. */
export const PROFILE_LABEL: Record<Profile, string> = {
  engineering: 'Engineering & IT',
  executive: 'Executive',
  product_gtm: 'Product & GTM',
  admin: 'Admin',
}

export type SettingsGroup = 'account' | 'workspace'

export interface SettingsSection {
  group: SettingsGroup
  id: string
  label: string
}

export const SETTINGS_SECTIONS: SettingsSection[] = [
  { group: 'account', id: 'profile', label: 'Profile' },
  { group: 'account', id: 'appearance', label: 'Appearance' },
  { group: 'workspace', id: 'general', label: 'General' },
  { group: 'workspace', id: 'members', label: 'Members' },
  { group: 'workspace', id: 'usage', label: 'Usage' },
  { group: 'workspace', id: 'scan-rules', label: 'Scan rules' },
]

export const GROUP_LABEL: Record<SettingsGroup, string> = {
  account: 'Your account',
  workspace: 'Workspace',
}

export function settingsPath(group: SettingsGroup, id: string): string {
  return `/settings/${group}/${id}`
}

/** Named destinations, so a link elsewhere in the app never spells a path by hand. */
export const SETTINGS_PATH = {
  profile: settingsPath('account', 'profile'),
  appearance: settingsPath('account', 'appearance'),
  general: settingsPath('workspace', 'general'),
  members: settingsPath('workspace', 'members'),
  usage: settingsPath('workspace', 'usage'),
  scanRules: settingsPath('workspace', 'scan-rules'),
} as const

export function findSection(group: string | undefined, id: string | undefined): SettingsSection | undefined {
  return SETTINGS_SECTIONS.find((s) => s.group === group && s.id === id)
}

/**
 * Where the old single page `/settings` (and an unknown section) now lands. Before
 * DEC-198 `/settings` was the admin's workspace page, and the GitHub sign-in still
 * returns there for a flow started earlier, so admins land on General, where the
 * GitHub banner is read. Everyone else lands on their Profile.
 */
export function defaultSettingsPath(isAdmin: boolean): string {
  return isAdmin ? SETTINGS_PATH.general : SETTINGS_PATH.profile
}
